import { useEffect, useRef } from "react";

const steps = [
  {
    no: "01",
    head: "Discovery",
    sub: "Understanding Your Business",
    body: "We start by learning your operations, goals, and challenges — identifying where technology and AI can make the biggest impact.",
    expect:
      "A partner who listens first. You'll gain clarity on what's possible, where the opportunities are, and a clear picture of how your business can evolve.",
    color: "#ff4500",
  },
  {
    no: "02",
    head: "Strategy",
    sub: "Building the Right Roadmap",
    body: "We define scope, technology, architecture, and priorities — ensuring every decision aligns with your business objectives and future growth.",
    expect:
      "A well-planned roadmap with no guesswork. You'll know exactly what's being built, why, and how it sets you up for scale.",
    color: "#ccff00",
  },
  {
    no: "03",
    head: "Design",
    sub: "Crafting Scalable Experiences",
    body: "Intuitive interfaces and robust system architectures designed for performance, usability, and long-term growth.",
    expect:
      "Designs that feel right and systems that last. You'll see your vision take shape through wireframes, user journeys, and architecture that's built for tomorrow.",
    color: "#ff4500",
  },
  {
    no: "04",
    head: "Development",
    sub: "Turning Concepts Into Systems",
    body: "We build responsive websites, applications, SaaS platforms, and AI-powered solutions using modern, clean code.",
    expect:
      "Regular progress, transparency, and a solution that's developed with quality and scalability at its core — not shortcuts.",
    color: "#ccff00",
  },
  {
    no: "05",
    head: "Testing",
    sub: "Ensuring Reliability & Performance",
    body: "Every solution is rigorously tested for performance, security, responsiveness, and usability before launch.",
    expect:
      "Confidence at launch. You'll know your platform is battle-tested, secure, and ready to perform across every device and environment.",
    color: "#ff4500",
  },
  {
    no: "06",
    head: "Launch & Growth",
    sub: "Deploying With Confidence",
    body: "We deploy your solution and stay on board with ongoing support, monitoring, and future enhancements.",
    expect:
      "A long-term partner, not a one-off delivery. You'll have continuous support, proactive improvements, and technology that evolves with your business.",
    color: "#ccff00",
  },
];

export default function Process() {
  const root = useRef(null);

  useEffect(() => {
    const els = root.current?.querySelectorAll(".reveal");
    if (!els) return;
    const io = new IntersectionObserver(
      (entries) =>
        entries.forEach(
          (e) => e.isIntersecting && e.target.classList.add("is-in")
        ),
      { threshold: 0.12 }
    );
    els.forEach((el) => io.observe(el));
    return () => io.disconnect();
  }, []);

  return (
    <section
      id="process"
      data-testid="section-process"
      ref={root}
      className="relative py-28 md:py-40 border-t border-[var(--border-dim)] overflow-hidden"
    >
      <div className="absolute inset-0 grid-bg opacity-15 pointer-events-none" />

      <div className="relative mx-auto max-w-7xl px-5 sm:px-8">
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-14">
          <div>
            <div className="flex items-center gap-3 mb-6 reveal">
              <span className="w-2 h-2 bg-[var(--lime)] rounded-full" />
              <span className="overline">— How we work</span>
            </div>
            <h2
              data-testid="process-headline"
              className="font-display font-black text-4xl sm:text-5xl md:text-6xl tracking-tight leading-[0.95] max-w-3xl reveal"
            >
              A clear path from{" "}
              <span className="italic font-normal text-[var(--text-2)]">
                idea
              </span>{" "}
              to <span className="text-[var(--vermilion)]">launch</span>.
            </h2>
          </div>
          <p className="text-[var(--text-2)] max-w-sm reveal">
            Six tightly-scoped phases. No surprises, no scope creep — just a
            predictable rhythm from discovery to deployment, and beyond.
          </p>
        </div>

        <ol className="relative space-y-px bg-[var(--border-dim)] rounded-sm overflow-hidden border border-[var(--border-dim)]">
          {steps.map((s, i) => (
            <li
              key={s.no}
              data-testid={`process-step-${s.no}`}
              className="group relative bg-[var(--bg)] hover:bg-[var(--bg-2)] transition-colors duration-500 reveal"
            >
              <div className="grid grid-cols-12 gap-6 px-7 md:px-10 py-10 md:py-12">
                {/* Number + meta */}
                <div className="col-span-12 md:col-span-3 flex md:flex-col items-baseline md:items-start gap-4 md:gap-2">
                  <span
                    className="font-display font-black text-6xl md:text-7xl tracking-tight leading-none"
                    style={{ color: s.color }}
                  >
                    {s.no}
                  </span>
                  <span className="font-mono-ui text-[10px] tracking-[0.22em] uppercase text-[var(--text-3)]">
                    Phase {i + 1} / {steps.length}
                  </span>
                </div>

                {/* Title + body */}
                <div className="col-span-12 md:col-span-5">
                  <h3 className="font-display font-bold text-2xl md:text-3xl tracking-tight">
                    {s.head}
                  </h3>
                  <div className="overline mt-1 mb-4">{s.sub}</div>
                  <p className="text-[var(--text-1)] text-sm md:text-base leading-relaxed">
                    {s.body}
                  </p>
                </div>

                {/* What to expect */}
                <div className="col-span-12 md:col-span-4">
                  <div
                    className="h-full border-l-2 pl-5"
                    style={{ borderColor: s.color }}
                  >
                    <div className="font-mono-ui text-[10px] tracking-[0.22em] uppercase text-[var(--text-3)] mb-2">
                      What to expect
                    </div>
                    <p className="text-[var(--text-2)] text-sm leading-relaxed">
                      {s.expect}
                    </p>
                  </div>
                </div>
              </div>
            </li>
          ))}
        </ol>
      </div>
    </section>
  );
}
